import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ClerkLoaded, ClerkLoading, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';

import shopApi from '@services/shop';
import { Loader } from '@/components/ui/loader';
import { AnalyticsWrapper } from '@/components/analytics-wrapper';

/**
 * Mirror of `dashboard-shared`'s `PrivateRouteComponent` for the Unify
 * dashboard. We can't reuse the shared one because that import chain pulls
 * `@nearst/ui` global styles back in (see `docs/design-record.md` §1.5).
 *
 *   - Clerk still loading → render the shared `Loader`
 *   - Signed out → redirect to `/login`
 *   - Signed in → render children
 *
 * Also fires `shopApi.updateLastLogin()` once per session — same hook as the
 * retailers-dashboard `AnalyticsWrapper`. We skip the call when an `act`
 * (impersonation) actor is present so HQ "view as" sessions don't bump the
 * retailer's last-seen timestamp.
 *
 * Dev-only resilience: if Clerk fails to hydrate (e.g. headless preview
 * without network access to `*.clerk.accounts.dev`), we fall through to the
 * dashboard after 4 s so design work can continue with mock data. The fallthrough
 * is gated on `import.meta.env.DEV` so it never reaches production.
 */
// DEV-only escape hatch for headless preview (Storybook, the design-guidelines
// route hosted in a sandbox without internet access to clerk.accounts.dev).
// `?nockerk=1` disables PrivateRoute for the current tab; the flag is sticky
// via `sessionStorage` so it survives client-side navigations. Disabled in
// production builds via `import.meta.env.DEV` so it can never reach prod.
function devBypassActive() {
	if (!import.meta.env.DEV || typeof window === 'undefined') return false;
	const params = new URLSearchParams(window.location.search);
	if (params.get('nockerk') === '1') {
		try {
			window.sessionStorage.setItem('unify.dev-bypass-clerk', '1');
		} catch {
			/* ignore */
		}
		return true;
	}
	try {
		return window.sessionStorage.getItem('unify.dev-bypass-clerk') === '1';
	} catch {
		return false;
	}
}

export function PrivateRoute({ children }) {
	const [devFallthrough, setDevFallthrough] = useState(devBypassActive);

	useEffect(() => {
		if (!import.meta.env.DEV) return undefined;
		const id = window.setTimeout(() => setDevFallthrough(true), 4000);
		return () => window.clearTimeout(id);
	}, []);

	if (devFallthrough) {
		return children;
	}

	return (
		<>
			<ClerkLoading>
				<Loader label="Loading your dashboard…" />
			</ClerkLoading>
			<ClerkLoaded>
				<SignedOut>
					<Navigate to="/login" replace />
				</SignedOut>
				<SignedIn>
					<AuthedShell>{children}</AuthedShell>
				</SignedIn>
			</ClerkLoaded>
		</>
	);
}

function AuthedShell({ children }) {
	const { isLoaded, sessionClaims } = useAuth();

	useEffect(() => {
		if (!isLoaded) return;
		// `act` is set by Clerk when the session is impersonated (HQ "view as").
		// Skip the last-login update so we don't taint the retailer's record.
		if (sessionClaims?.act) return;
		shopApi.updateLastLogin().catch((err) => {
			// Last-login is best-effort — log and move on.
			console.warn('updateLastLogin failed', err);
		});
	}, [isLoaded, sessionClaims?.act]);

	if (!isLoaded) {
		return <Loader label="Loading your dashboard…" />;
	}
	return <AnalyticsWrapper>{children}</AnalyticsWrapper>;
}
